import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { PageLayout } from '@cvx/nextpage';
import { PelotonAdminInstance, PelotonAdminSvc } from 'src/app/services/pelotonAdmin/pelotonAdmin.service';

@Component({
  selector: 'access-dashboard',
  templateUrl: './access-dashboard.component.html',
  styleUrls: ['./access-dashboard.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class AccessDashboardComponent implements OnInit {
  PageLayout = PageLayout;

  readonly instances: PelotonAdminInstance[] = ['NA', 'NA-Test', 'East', 'East-Test'];

  selectedInstance: PelotonAdminInstance = 'NA-Test';
  groups: Array<{ id: string; name: string }> = [];
  selectedGroup = '';
  details: Record<string, unknown> | null = null;
  groupId = '';
  groupName = '';
  groupDescription = '';
  members: string[] = [];
  permissionRows: Array<{ key: string; value: boolean }> = [];
  securityProfiles: Array<{ id: string; name: string }> = [];
  securityTypes: Array<{ id: string; name: string; deleteEntity: boolean; readOnly: boolean }> = [];
  detailsRows: Array<{ key: string; value: string }> = [];
  additionalRows: Array<{ key: string; value: string }> = [];
  loadingDetails = false;
  errorMessage = '';

  constructor(private pelotonAdminSvc: PelotonAdminSvc) {}

  ngOnInit(): void {
    this.loadGroups();
  }

  onInstanceChange(): void {
    this.selectedGroup = '';
    this.details = null;
    this.resetDetailsView();
    this.errorMessage = '';
    this.loadGroups();
  }

  selectGroup(group: { id: string; name: string }): void {
    this.selectedGroup = group.name;
    this.errorMessage = '';
    this.loadingDetails = true;

    this.pelotonAdminSvc.getPelotonGroupDetails(this.selectedInstance, group.name).subscribe({
      next: (response) => {
        this.loadingDetails = false;
        this.details = response;
        this.mapDetailsToView(response);
      },
      error: () => {
        this.loadingDetails = false;
        this.details = null;
        this.resetDetailsView();
        this.errorMessage = 'Unable to retrieve group details. Please try again.';
      }
    });
  }

  private loadGroups(): void {
    this.groups = [];
    this.pelotonAdminSvc.getPelotonAdminGroups(this.selectedInstance).subscribe({
      next: (groups) => {
        this.groups = groups;
      },
      error: () => {
        this.errorMessage = 'Unable to retrieve groups. Please try again.';
      }
    });
  }

  toDisplayLabel(rawKey: string): string {
    if (!rawKey) {
      return '-';
    }

    const spaced = rawKey
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();

    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  private formatValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return JSON.stringify(value);
  }

  private mapDetailsToView(details: Record<string, unknown>): void {
    this.resetDetailsView();

    this.detailsRows = Object.keys(details)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({ key, value: this.formatValue(details[key]) }));

    this.groupId = typeof details['id'] === 'string' ? details['id'] : '-';
    this.groupName = typeof details['name'] === 'string' ? details['name'] : this.selectedGroup;
    this.groupDescription = typeof details['description'] === 'string' ? details['description'] : '-';

    const members = details['members'];
    if (Array.isArray(members)) {
      this.members = members
        .filter((member): member is string => typeof member === 'string')
        .sort((a, b) => a.localeCompare(b));
    }

    const permissions = details['permissions'];
    if (permissions && typeof permissions === 'object' && !Array.isArray(permissions)) {
      const permissionRecord = permissions as Record<string, unknown>;
      this.permissionRows = Object.keys(permissionRecord)
        .sort((a, b) => a.localeCompare(b))
        .filter((key) => typeof permissionRecord[key] === 'boolean')
        .map((key) => ({ key, value: permissionRecord[key] as boolean }));
    }

    const securityProfiles = details['securityProfiles'];
    if (Array.isArray(securityProfiles)) {
      this.securityProfiles = securityProfiles
        .filter((profile): profile is Record<string, unknown> => !!profile && typeof profile === 'object' && !Array.isArray(profile))
        .map((profile) => ({
          id: typeof profile['id'] === 'string' ? profile['id'] : '-',
          name: typeof profile['name'] === 'string' ? profile['name'] : '-'
        }));
    }

    const securityTypes = details['securityTypes'];
    if (Array.isArray(securityTypes)) {
      this.securityTypes = securityTypes
        .filter((securityType): securityType is Record<string, unknown> => !!securityType && typeof securityType === 'object' && !Array.isArray(securityType))
        .map((securityType) => {
          const permissionsObj = securityType['permissions'];
          const permissionsMap = permissionsObj && typeof permissionsObj === 'object' && !Array.isArray(permissionsObj)
            ? permissionsObj as Record<string, unknown>
            : {};

          return {
            id: typeof securityType['id'] === 'string' ? securityType['id'] : '-',
            name: typeof securityType['name'] === 'string' ? securityType['name'] : '-',
            deleteEntity: permissionsMap['deleteEntity'] === true,
            readOnly: permissionsMap['readOnly'] === true
          };
        });
    }

    const handledKeys = new Set(['id', 'name', 'description', 'members', 'permissions', 'securityProfiles', 'securityTypes']);
    this.additionalRows = Object.keys(details)
      .filter((key) => !handledKeys.has(key))
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({ key, value: this.formatValue(details[key]) }));
  }

  private resetDetailsView(): void {
    this.groupId = '';
    this.groupName = '';
    this.groupDescription = '';
    this.members = [];
    this.permissionRows = [];
    this.securityProfiles = [];
    this.securityTypes = [];
    this.detailsRows = [];
    this.additionalRows = [];
  }
}
